import { packagesNaming } from "../../PreviewPageCheckout/components/ComposePackages/Packages/utils/packages-naming";
import { RecordCheckTrackingData } from "./dataLayerECommerceEvents.types";
import {
  VinregSearchType,
  type DataLayerCalculation,
  type DataLayerPageView,
  type DataLayerPageViewExternal,
  type DataLayerPaymentErrorExternal,
  LoginMethod,
  LoginProviders,
  B2BSignupType,
  SignupEntryType,
} from "./dataLayerEvents.types";
import { dataLayerUtil } from "./utils/dataLayerUtil";
import { pushToDataLayer } from "./utils/pushToDataLayer";
import userStore from "stores/user.store";
import {
  getStoredUtmParams,
  clearStoredUtmParams,
  getStoredCtParams,
  clearStoredCtParams,
  getStoredVin,
  clearStoredVin,
  getStoredUtmReferrer,
  setStoredUtmReferrer,
} from "utils/text-link/TextLinkParamsContext";

const PARTNER_TEXT_LINK_PATTERN = /^(\/[a-z]{2})?\/partner\/[^/]+\/[^/]+$/;
const INTEGRATION_TEXT_LINK_PATTERN = /^(\/[a-z]{2})?\/business\/integration\/[^/]+\/[^/]+$/;

const stripTextLinkId = (url: URL): void => {
  if (PARTNER_TEXT_LINK_PATTERN.test(url.pathname) || INTEGRATION_TEXT_LINK_PATTERN.test(url.pathname)) {
    url.pathname = url.pathname.replace(/\/[^/]+$/, "");
  }
};

const buildPageLocationWithUtm = (): string => {
  const url = new URL(window.location.href);
  const utmParams = getStoredUtmParams();
  const ctParams = getStoredCtParams();
  const vin = getStoredVin();
  const hasUtm = Object.keys(utmParams).length > 0;
  const hasCt = Object.keys(ctParams).length > 0;
  const hasTextLinkData = hasUtm || hasCt || vin !== null;

  if (hasTextLinkData) {
    stripTextLinkId(url);

    for (const key of Object.keys(utmParams)) {
      if (!url.searchParams.has(key)) {
        url.searchParams.set(key, utmParams[key]);
      }
    }

    for (const key of Object.keys(ctParams)) {
      if (!url.searchParams.has(key)) {
        url.searchParams.set(key, ctParams[key]);
      }
    }

    if (vin !== null && !url.searchParams.has("vin")) {
      url.searchParams.set("vin", vin ? "true" : "false");
    }

    const enrichedUrl = url.toString();
    setStoredUtmReferrer(enrichedUrl);
    clearStoredUtmParams();
    clearStoredCtParams();
    clearStoredVin();

    return enrichedUrl;
  }

  return url.toString();
};

const stripTextLinkIdFromPath = (path: string): string => {
  if (PARTNER_TEXT_LINK_PATTERN.test(path) || INTEGRATION_TEXT_LINK_PATTERN.test(path)) {
    return path.replace(/\/[^/]+$/, "");
  }
  return path;
};

const resolvePageReferrer = (pageReferrer: string | undefined): string | undefined => {
  const utmReferrer = getStoredUtmReferrer();

  if (!utmReferrer || !pageReferrer) {
    return pageReferrer;
  }

  const referrerPath = stripTextLinkIdFromPath(new URL(pageReferrer).pathname);
  const utmReferrerPath = new URL(utmReferrer).pathname;

  if (referrerPath === utmReferrerPath) {
    return utmReferrer;
  }

  return pageReferrer;
};

const submitPageViewEvent = ({
  pageReferrer,
  contentGroup,
  pageLanguage,
  vinregType,
  vinRecords,
  reportCombination,
  vinregSearchType,
}: DataLayerPageView): void => {
  const eventParameters: DataLayerPageViewExternal = {
    event: "page_view",
    page_location: buildPageLocationWithUtm(),
    page_referrer: resolvePageReferrer(pageReferrer),
    content_group: contentGroup,
    page_language: pageLanguage,
    vin_type: vinregType,
    vin_records: vinRecords,
    report_combination: reportCombination,
    vin_method: vinregSearchType,
    user_type: userStore.userTrackingType,
    user_id: userStore.userTrackingId,
  };
  pushToDataLayer(eventParameters);
};

const submitBolloCalculationEvent = ({ event, isVinSearch, errorMessage }: DataLayerCalculation): void => {
  const eventParameters = {
    event,
    calculation_type: isVinSearch ? "with_vin" : "without_vin",
    ...(errorMessage && { error_message: errorMessage }),
  };
  pushToDataLayer(eventParameters);
};

const submitSwitchBolloCalculatorTab = (eventWithVin: boolean): void => {
  const eventParameters = {
    event: "click_calculation_tab",
    click_text: eventWithVin ? "with_vin" : "without_vin",
  };
  pushToDataLayer(eventParameters);
};

const submitViewSampleReportEvent = (): void => {
  const eventParameters = {
    event: "view_sample_report",
  };
  pushToDataLayer(eventParameters);
};

const submitInputVoucherSuccessEvent = (voucherCode: string, voucherDiscountPercent: number): void => {
  const eventParameters = {
    event: "input_voucher_success",
    coupon: `${voucherCode} | ${voucherDiscountPercent}%`,
  };
  pushToDataLayer(eventParameters);
};

const submitInputVoucherErrorEvent = (voucherCode: string, voucherDiscountPercent: number): void => {
  const eventParameters = {
    event: "input_voucher_error",
    coupon: `${voucherCode} | ${voucherDiscountPercent}%`,
  };
  pushToDataLayer(eventParameters);
};

const vinSearchErrorEvent = (vinregSearchType?: string): void => {
  const eventParameters = {
    event: "vin_search_error",
    vin_method: vinregSearchType ?? VinregSearchType.MANUALLY,
  };
  pushToDataLayer(eventParameters);
};

const vinSearchSuccessEvent = (
  recordCheckTrackingData: RecordCheckTrackingData = {},
  vinregSearchType?: VinregSearchType,
): void => {
  const { reportDataTotal = undefined, vinreg = undefined, reportCombination = undefined } = recordCheckTrackingData;

  const eventParameters = {
    event: "vin_search_success",
    vin_method: vinregSearchType ?? VinregSearchType.MANUALLY,
    vin_records: reportDataTotal,
    vin_type: dataLayerUtil.getVinregType(vinreg),
    report_combination: reportCombination,
  };
  pushToDataLayer(eventParameters);
};

const confirmRadioMissingEvent = (): void => {
  const eventParameters = {
    event: "confirm_radio_missing",
  };
  pushToDataLayer(eventParameters);
};

const submitPaymentErrorEvent = (paymentType: string, packageName: string, errorMessage: string): void => {
  const eventParameters: DataLayerPaymentErrorExternal = {
    event: "payment_error_viewed",
    payment_type: paymentType,
    package_name: packagesNaming.resolvePackageNameForTracking(packageName),
    error_message: errorMessage.substring(0, 100),
  };
  pushToDataLayer(eventParameters);
};

const submitButtonRedirectToPackagesEvent = (): void => {
  const eventParameters = {
    event: "click_select_package",
  };
  pushToDataLayer(eventParameters);
};

const inputTaxNumberErrorEvent = (): void => {
  const eventParameters = {
    event: "input_tax_number_error",
    error_message: "Invalid tax number",
  };
  pushToDataLayer(eventParameters);
};

const inputTaxNumberSuccessEvent = (): void => {
  const eventParameters = {
    event: "input_tax_number_success",
  };
  pushToDataLayer(eventParameters);
};

const submitSignupSuccessBusinessEvent = (signupType: B2BSignupType): void => {
  const eventParameters = {
    event: "b2b_signup_success",
    signup_type: signupType,
  };
  pushToDataLayer(eventParameters);
};

const submitSignupFromEntryB2BEvent = (entryType: SignupEntryType): void => {
  const eventParameters = {
    event: "b2b_signup_entry",
    entry_type: entryType,
  };
  pushToDataLayer(eventParameters);
};

const submitDocumentUploadBusinessEvent = (signupType: B2BSignupType): void => {
  const eventParameters = {
    event: "b2b_signup_document_upload",
    signup_type: signupType,
  };
  pushToDataLayer(eventParameters);
};

const submitLoginEvent = (loginMethod: LoginMethod, loginProvider: LoginProviders) => {
  const eventParameters = {
    event: "login",
    login_method: loginMethod,
    login_service: loginProvider,
    user_type: userStore.userTrackingType,
    user_id: userStore.userTrackingId,
  };
  pushToDataLayer(eventParameters);
};

const submitAiZoomTriggeredEvent = (): void => {
  const eventParameters = {
    event: "ai_zoom_triggered",
  };
  pushToDataLayer(eventParameters);
};

const submitAiZoomNotTriggeredEvent = (): void => {
  const eventParameters = {
    event: "ai_zoom_not_triggered",
  };
  pushToDataLayer(eventParameters);
};

export const dataLayerEvents = {
  submitPageViewEvent,
  submitBolloCalculationEvent,
  submitViewSampleReportEvent,
  vinSearchSuccessEvent,
  vinSearchErrorEvent,
  confirmRadioMissingEvent,
  submitInputVoucherSuccessEvent,
  submitInputVoucherErrorEvent,
  submitSwitchBolloCalculatorTab,
  submitPaymentErrorEvent,
  submitButtonRedirectToPackagesEvent,
  inputTaxNumberErrorEvent,
  inputTaxNumberSuccessEvent,
  submitSignupSuccessBusinessEvent,
  submitSignupFromEntryB2BEvent,
  submitLoginEvent,
  submitAiZoomTriggeredEvent,
  submitAiZoomNotTriggeredEvent,
  submitDocumentUploadBusinessEvent,
};
